import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import ReportHeader from '../shared/ReportHeader';
import ReportTabs from './ReportTabs';
import ReportSection from './ReportSection';
import ReportTimeline from './ReportTimeline';
import ErrorMessage from '../../shared/ErrorMessage';
import { saveReport } from '../../../services/agentcore.service';
import { createIdea } from '../../../services/idea.service';
import { showSuccess, showError } from '../../../services/notification.service';

const ReportViewer = ({ reportData, onNewReport, needsApproval = false }) => {
  const { reportId } = useParams();
  const [activeTab, setActiveTab] = useState('overview');
  const [activeSection, setActiveSection] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [reportSaved, setReportSaved] = useState(false);  // Track if report is saved

  const buildIdeaFromReport = () => {
    const title = (reportData?.title || reportData?.idea || reportData?.idea_title || 'Idea from Analyzer').trim();
    const summarySection = (reportData?.sections || []).find((s) => s?.section_type === 'idea_summary');
    const description = (
      reportData?.summary ||
      summarySection?.content ||
      reportData?.idea ||
      ''
    );

    return {
      title,
      description,
      category: reportData?.category || '',
      tags: Array.isArray(reportData?.tags) ? reportData.tags : [],
      questionnaire_data: reportData?.questionnaire_data || {},
      is_draft: false,
    };
  };

  // Handle save report
  const handleSaveReport = async () => {
    setIsSaving(true);
    setSaveError(null);
    
    try {
      await saveReport(reportData);

      // Keep Idea tab in sync with Idea Analyzer output.
      if (!reportData?._ideaSynced) {
        try {
          await createIdea(buildIdeaFromReport());
        } catch (ideaErr) {
          // Non-blocking: report save should still succeed even if idea sync fails.
          console.warn('Idea sync from report save failed:', ideaErr);
        }
      }

      setSaveSuccess(true);
      setReportSaved(true);  // Mark report as saved
      
      // Clean up job report data from sessionStorage after successful save
      if (reportId) {
        sessionStorage.removeItem(`jobReport_${reportId}`);
      }
      
      showSuccess('Report saved successfully!');
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to save report:', error);
      const errorMsg = 'Failed to save report. Please try again.';
      setSaveError(errorMsg);
      showError(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  // Validate report data
  if (!reportData || !reportData.sections || !Array.isArray(reportData.sections)) {
    return (
      <div className="report-display">
        <ErrorMessage 
          message="The report data is missing or malformed. Please try generating the report again." 
        />
        <button onClick={onNewReport} className="generate-btn" style={{ marginTop: '1rem' }}>
          Generate New Report
        </button>
      </div>
    );
  }

  const handleSectionClick = (index) => {
    setActiveSection(index);
    document.getElementById(`section-${index}`)?.scrollIntoView({ 
      behavior: 'smooth',
      block: 'start'
    });
  };

  // Sort sections to ensure Idea Summary appears first
  const getSortedSections = (sections) => {
    const sectionOrder = [
      'idea_summary',
      'technical_requirements',
      'market_analysis',
      'performance_applications',
      'research_papers',
      'regulatory_compliance',
      'financial_analysis',
      'risk_assessment',
      'competitor_analysis',
      'executive_summary'
    ];
    
    return [...sections].sort((a, b) => {
      const aIndex = sectionOrder.indexOf(a.section_type);
      const bIndex = sectionOrder.indexOf(b.section_type);
      
      // If section type not in order, put it at the end
      const aOrder = aIndex === -1 ? 999 : aIndex;
      const bOrder = bIndex === -1 ? 999 : bIndex;
      
      return aOrder - bOrder;
    });
  };

  const renderTabContent = () => {
    if (activeTab === 'overview') {
      const sortedSections = getSortedSections(reportData.sections);
      
      return (
        <div className="overview-layout">
          <div className="timeline-sidebar">
            <ReportTimeline 
              sections={sortedSections}
              activeSection={activeSection}
              onSectionClick={handleSectionClick}
            />
          </div>
          <div className="report-content-main">
            {sortedSections.map((section, index) => (
              <ReportSection key={index} section={section} index={index} />
            ))}
          </div>
        </div>
      );
    } else {
      const sectionIndex = parseInt(activeTab.replace('section-', ''));
      const section = reportData.sections[sectionIndex];
      
      if (!section) return <div>Section not found</div>;

      return (
        <div className="single-section-content">
          <ReportSection section={section} index={sectionIndex} />
        </div>
      );
    }
  };

  return (
    <div className="report-display">
      <ReportHeader reportData={reportData} onNewReport={onNewReport} />
      
      {/* Save Report Button - Only show if needs approval and not yet saved */}
      {needsApproval && !reportSaved && !saveSuccess && (
        <div className="save-approval-banner">
          <div className="approval-content">
            <span className="approval-icon">💾</span>
            <div className="approval-text">
              <strong>Report Generated Successfully!</strong>
              <p>Review your report and save it to the database for future reference.</p>
            </div>
            <button 
              onClick={handleSaveReport} 
              disabled={isSaving}
              className="save-approval-btn"
            >
              {isSaving ? '⏳ Saving...' : '✅ Save Report'}
            </button>
          </div>
          {saveError && (
            <div className="save-error">{saveError}</div>
          )}
        </div>
      )}

      {/* Save Success Message */}
      {saveSuccess && (
        <div className="save-success-banner">
          <span className="success-icon">✅</span>
          <span>Report saved successfully to database!</span>
        </div>
      )}
      
      <ReportTabs 
        sections={reportData.sections}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />
      
      <div className="tab-content">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default ReportViewer;
